// "Which location do they work with?" when adding a contact by hand
// (Stephanie 2026-10-20: "when adding manually we also need to be able to
// assign them to a location"). Shows only on a main account that has
// locations (Bozzuto); required there. Pick a location, the main account, or
// "+ Add a new location" (a new sub-account with the main account's rep).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/Input";
import type { CompanyLocations } from "@/components/website/LocationPicker";

export interface ContactLocationValue {
  /** "" = not picked yet; a client id; or "new". */
  pick: string;
  newName: string;
}

export const emptyLocation: ContactLocationValue = { pick: "", newName: "" };

/** The company's locations — for the main account or one of its locations
 *  (null for a plain account). isMain: this account is the main one. */
export function useClientLocations(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "locations"],
    queryFn: () => api<{ company: CompanyLocations | null; isMain: boolean }>(`/clients/${clientId}/locations`),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

/** Pure: is the choice complete, and what to send with the new contact. */
export function locationPayload(company: CompanyLocations | null | undefined, v: ContactLocationValue):
  | { ok: true; body: { location_client_id?: string; new_location_name?: string } }
  | { ok: false; error: string } {
  if (!company) return { ok: true, body: {} };
  if (v.pick === "new") {
    return v.newName.trim().length >= 2
      ? { ok: true, body: { new_location_name: v.newName.trim() } }
      : { ok: false, error: "Type the new location's name." };
  }
  if (!v.pick) return { ok: false, error: `Pick which ${company.main.name} location they work with.` };
  return { ok: true, body: { location_client_id: v.pick } };
}

/** Pure: the locations a search offers — every word typed, any order/case;
 *  the main account too; at most 8. Blank → none (Bozzuto has ~430). */
export function locationMatches(company: CompanyLocations, query: string, limit = 8): Array<{ id: string; label: string }> {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const all = [
    ...company.locations.map((l) => ({ id: l.id, label: l.name })),
    { id: company.main.id, label: `${company.main.name} (main account)` },
  ];
  return all.filter((a) => words.every((w) => a.label.toLowerCase().includes(w))).slice(0, limit);
}

// A search box, not a dropdown (Stephanie 2026-10-30: the long native list
// drew blank in Chrome, and Bozzuto will have ~430 buildings). Type part of
// the name, click it; "+ Add a new location" is always offered.
export function ContactLocationSelect({
  company,
  value,
  onChange,
  labelClassName = "mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400",
  label = "Location (required)",
}: {
  company: CompanyLocations;
  value: ContactLocationValue;
  onChange: (v: ContactLocationValue) => void;
  labelClassName?: string;
  label?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const picked =
    value.pick === "new"
      ? `+ New location`
      : value.pick === company.main.id
        ? `${company.main.name} (main account)`
        : company.locations.find((l) => l.id === value.pick)?.name ?? null;
  const matches = locationMatches(company, query);
  const choose = (pick: string): void => {
    onChange({ ...value, pick });
    setQuery("");
    setOpen(false);
  };
  return (
    <div className="grid gap-2">
      <div>
        <span className={labelClassName}>{label}</span>
        {picked && !open ? (
          <div className="flex h-10 items-center justify-between rounded-md border border-rule bg-white px-3 text-sm text-ink">
            <span>{picked}</span>
            <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-ink hover:underline">
              Change
            </button>
          </div>
        ) : (
          <div className="rounded-md border border-rule bg-white">
            <Input
              autoFocus={open}
              aria-label="Search locations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Type a ${company.main.name} location`}
              className="border-0"
            />
            <ul className="border-t border-rule">
              {matches.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => choose(m.id)} className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-peach-50">
                    {m.label}
                  </button>
                </li>
              ))}
              {query.trim() && matches.length === 0 && (
                <li className="px-3 py-2 text-xs text-muted">No location matches “{query.trim()}”.</li>
              )}
              <li>
                <button type="button" onClick={() => choose("new")} className="block w-full px-3 py-2 text-left text-sm font-medium text-ink hover:bg-peach-50">
                  + Add a new {company.main.name} location
                </button>
              </li>
              {picked && (
                <li>
                  <button type="button" onClick={() => { setQuery(""); setOpen(false); }} className="block w-full px-3 py-2 text-left text-xs text-muted hover:text-ink">
                    Keep {picked}
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {value.pick === "new" ? (
        <label className="block">
          <span className={labelClassName}>New location name</span>
          <Input
            value={value.newName}
            onChange={(e) => onChange({ ...value, newName: e.target.value })}
            maxLength={200}
            placeholder={`e.g. ${company.main.name} – The Elms`}
          />
          <span className="mt-1 block text-xs text-muted">
            Added under {company.main.name}
            {company.main.repName ? `, with ${company.main.repName} as the rep` : ""}.
          </span>
        </label>
      ) : null}
    </div>
  );
}
